import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faLayerGroup, faPlus, faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";

interface MyListsEmptyStateProps {
	/** Start the list-creation flow (primary action). */
	onCreateList: () => void;
	/** Switch to the All lists tab (secondary action). */
	onSeeAllLists: () => void;
}

/**
 * Onboarding empty state for the List overview when the user's own "My Lists" scope is empty
 * (PREC-3623, mockup "signalist-empty-mylists-v3"). Rendered in the table's centered empty slot,
 * mirroring the PREC-3542 {@link ListEmptyState} styling.
 *
 * Only shown for the *unfiltered* My Lists view; Team / All and any filtered-to-zero result fall
 * back to the table's default "no data" message. Replaces the previous auto-switch-to-All behaviour.
 */
export const MyListsEmptyState: FunctionComponent<MyListsEmptyStateProps> = ({
	onCreateList,
	onSeeAllLists,
}) => {
	const { t } = useTranslation();
	const te = (key: string) => t(`list.emptyState.myLists.${key}`);

	return (
		<div className="mx-auto flex max-w-md flex-col items-center rounded-2xl border border-border-light bg-white px-11 py-10 text-center shadow-sm">
			<div className="mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-[18px] bg-light-blue">
				<Icon className="h-8 w-8 text-brand-light" icon={faSparkles} />
			</div>

			<h2 className="mb-2.5 text-lg font-bold text-text">{te("title")}</h2>
			<p className="mb-6 text-sm leading-6 text-text-meta">{te("description")}</p>

			<div className="flex flex-wrap items-center justify-center gap-2.5">
				<Button
					color={ButtonColor.ACTION}
					image={<Icon className="mr-2" icon={faPlus} />}
					title={te("createList")}
					onClick={onCreateList}
				/>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={faLayerGroup} />}
					title={te("seeAllLists")}
					onClick={onSeeAllLists}
				/>
			</div>
		</div>
	);
};
